/**
 * TraderPicker.jsx — choose a trader to copy.
 *
 * Reads the same leaderboard the web app uses. Addresses are shown truncated
 * only: a full EOA ties a username to a permanent, searchable on-chain
 * identity, and there is no reason to expose that here.
 */

import React, { useEffect, useMemo, useState } from "react";
import * as api from "../lib/api";
import { C, money, pnlColor, shortAddr, Label, Panel, Empty } from "../theme";

export default function TraderPicker({ onPick }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/api/leaderboard/users", { mode: "all", limit: 100 })
      .then((d) => setRows(Array.isArray(d) ? d : d?.users || []))
      .catch((e) => setErr(e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const s = q.trim().toLowerCase();
    const base = s
      ? rows.filter((r) =>
          (r.username || "").toLowerCase().includes(s) ||
          (r.eoa_address || "").toLowerCase().includes(s))
      : rows;
    return base.slice(0, 60);
  }, [rows, q]);

  if (err) return <Empty><span style={{ color: C.red }}>{err}</span></Empty>;
  if (!rows) return <Empty>Loading traders…</Empty>;

  return (
    <div style={{ padding: 14 }}>
      <Label>SEARCH</Label>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Username or address"
        style={{
          width: "100%", boxSizing: "border-box", padding: "11px 12px",
          background: C.bg1, border: `0.5px solid ${C.border}`,
          color: C.ink, fontFamily: C.mono, fontSize: 13,
          marginBottom: 16, outline: "none",
        }}
      />

      <Label>TOP TRADERS</Label>
      {filtered.length === 0 && <Empty>No traders match that.</Empty>}

      {filtered.map((r) => {
        const pnl = Number(r.total_pnl || 0);
        return (
          <Panel key={r.user_id || r.eoa_address} onClick={() => onPick(r)}
                 accent={pnlColor(pnl)} style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 13, color: C.ink, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.username || shortAddr(r.eoa_address)}
              </span>
              <span style={{ fontSize: 14, color: pnlColor(pnl), flexShrink: 0 }}>
                {money(pnl)}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, fontSize: 11, color: C.dim,
                          flexWrap: "wrap" }}>
              <span>{Number(r.win_rate || 0).toFixed(0)}% win</span>
              <span>· {r.total_trades || 0} trades</span>
              <span>· {shortAddr(r.eoa_address)}</span>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}
